import { HttpService } from '@ribajs/core'
import { EventDispatcher } from '@ribajs/events'
import { isObject, clone, getNumber } from '@ribajs/utils'
import { ShopifyProductService } from './index.js'
import { PQueue } from '@ribajs/shopify' // https://github.com/sindresorhus/p-queue

import type {
  ShopifyCartObject,
  ShopifyProduct,
  ShopifyCartLineItem,
  ShopifyCartAction,
  ShopifyCartUpdateProperty,
  LineItemProperties,
  ValidateQuantityResult,
  ShopifyProductVariant,
} from '../types/index.js'
import { VALIDATE_QUANTITY_ERROR } from '../constants.js'
import type {
  ShopifyCartAddError,
  ShopifyCustomerAddress,
  ShopifyShippingRates,
  ShopifyShippingRate,
  ShopifyShippingRatesNormalized,
} from '@ribajs/shopify'

export interface ShopifyCartRequestOptions {
  triggerOnStart: boolean
  triggerOnComplete: boolean
  triggerOnChange: boolean
}

// TODO: Fix debounce from @ribajs/utils, it only uses the first passed arguments, not the last
function debounce<T extends (...args: any[]) => Promise<any>>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => Promise<ReturnType<T>> {
  let timeout: ReturnType<typeof setTimeout> | null

  return function (...args: Parameters<T>): Promise<ReturnType<T>> {
    return new Promise((resolve, reject) => {
      const later = () => {
        timeout = null
        try {
          resolve(func(...args))
        } catch (error) {
          reject(error)
        }
      }

      if (timeout) {
        clearTimeout(timeout)
      }
      timeout = setTimeout(later, wait)
    })
  }
}

const ROOT = window.Shopify?.routes?.root || '/'

export class ShopifyCartService {
  protected static CART_POST_ADD_URL = ROOT + 'cart/add.js'

  protected static CART_GET_URL = ROOT + 'cart.js'

  protected static CART_POST_UPDATE_URL = ROOT + 'cart/update.js'

  protected static CART_POST_CHANGE_URL = ROOT + 'cart/change.js'

  protected static CART_POST_CLEAR_URL = ROOT + 'cart/clear.js'

  protected static CART_GET_SHIPPING_RATES_URL =
    ROOT + 'cart/shipping_rates.json'

  public static queue = new PQueue({ concurrency: 1 })

  protected static cart: ShopifyCartObject | null = null

  public static events = new EventDispatcher('ShopifyCart')

  /**
   * Use this method to force an update of the shopping cart object, e.g. if the shopping cart was updated outside Riba.
   * @param options
   */
  public static async updateExtern(
    options: ShopifyCartRequestOptions = this.requestOptionDefaults,
  ) {
    if (options.triggerOnStart) {
      this.triggerOnStart()
    }

    const newCart = await this.refresh()
    const oldCart = this.cart
    this.cart = this.set(newCart)

    if (options.triggerOnChange) {
      if (
        oldCart?.total_price !== newCart?.total_price ||
        oldCart?.item_count !== oldCart?.item_count
      ) {
        this.triggerOnChange(this.cart)
      }
    }

    if (options.triggerOnComplete) {
      this.triggerOnComplete('update')
    }
  }

  public static getLineItem(variantId: number) {
    return this.cart?.items.find(item => item.variant_id === variantId)
  }

  /**
   * Use this to add a variant to the cart.
   * @param product Shopify product object
   * @param variantId Variant id
   * @param quantity Quantity
   * @param properties Additional properties
   * @return Response if successful, the JSON of the line item associated with the added variant.
   * @see https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#add-to-cart
   */
  public static async add(
    product: ShopifyProduct,
    variantId: number,
    options?: { quantity?: number; sellingPlanId?: number },
    properties: LineItemProperties = {},
    requestOptions: ShopifyCartRequestOptions = this.requestOptionDefaults,
  ): Promise<ShopifyCartLineItem | ShopifyCartAddError> {
    if (requestOptions.triggerOnStart) {
      this.triggerOnStart()
    }

    const quantity = options?.quantity || 1

    const promise = this.queue.add(async () => {
      const item: any = {
        id: variantId,
        quantity,
        selling_plan: options?.sellingPlanId,
      }

      // Convert properties to strings
      const propertyKeys = Object.keys(
        properties,
      ) as (keyof LineItemProperties)[]
      if (propertyKeys.length !== 0) {
        item.properties = {}
        for (const key of propertyKeys) {
          if (properties[key] !== undefined) {
            item.properties[key] = properties[key]?.toString()
          }
        }
      }
      // TODO add support for multiple items in array
      const lineItemRes = await HttpService.post<
        ShopifyCartLineItem | ShopifyCartAddError
      >(this.CART_POST_ADD_URL, { items: [item] }, 'json')

      if (lineItemRes.status >= 400) {
        throw lineItemRes.body as ShopifyCartAddError
      }

      const lineItem = lineItemRes.body

      // Force update cart object
      const cartRes = await HttpService.get<ShopifyCartObject>(
        this.CART_GET_URL,
        {},
        'json',
      )

      if (cartRes.status >= 400) {
        throw lineItemRes.body as ShopifyCartAddError
      }

      const cart = cartRes.body
      if (requestOptions.triggerOnChange) {
        this.triggerOnChange(cart)
      }

      // TODO: Add selling plan info?
      this.triggerAdd(product, variantId, quantity, properties, cart)
      return lineItem // return original response
    })

    if (requestOptions.triggerOnComplete) {
      this.triggerOnComplete('add')
    }

    return promise
  }

  public static async refresh(): Promise<ShopifyCartObject> {
    const cartRes = await HttpService.get<ShopifyCartObject>(
      this.CART_GET_URL,
      {},
      'json',
    )

    let cart: ShopifyCartObject | null

    if (typeof cartRes.body === 'string') {
      cart = JSON.parse(cartRes.body)
    } else {
      cart = cartRes.body
    }

    if (!cart) {
      throw new Error('The cart object is falsy!')
    }

    cart = this.set(cart)

    if (!cart) {
      throw new Error('The cart object is falsy!')
    }

    return cart
  }

  public static async _get(): Promise<ShopifyCartObject> {
    if (this.cart !== null) {
      return new Promise((resolve /*, reject*/) => {
        setTimeout(() => {
          if (this.cart !== null) {
            return resolve(this.cart)
          } else {
            return this._get()
          }
        }, 0)
      })
    }
    return this.refresh()
  }

  /**
   * Get the cart object
   * @param options
   * @return The JSON of the cart.
   * @see https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#get-cart
   */
  public static async get(
    options: ShopifyCartRequestOptions = this.requestOptionDefaults,
  ): Promise<ShopifyCartObject> {
    if (options.triggerOnStart) {
      this.triggerOnStart()
    }
    const promise = this.queue.add(() => {
      return this._get()
    })

    if (options.triggerOnComplete) {
      this.triggerOnComplete('get')
    }
    return promise
  }

  /** Set the cart object */
  protected static set(cart: ShopifyCartObject) {
    cart = this.prepareLineItems(cart)
    cart = this.merge(cart) || cart
    cart = this.prepareLineItems(cart)
    return cart
  }

  protected static mergeLineItem(
    oldLT: ShopifyCartLineItem,
    newLT: ShopifyCartLineItem,
  ) {
    for (const key of Object.keys(newLT) as Array<keyof ShopifyCartLineItem>) {
      if (oldLT[key] !== newLT[key]) {
        ;(oldLT[key] as ShopifyCartLineItem[typeof key]) = newLT[key]
      }
    }
    return oldLT
  }

  protected static mergeLineItems(
    oldLTs: Array<ShopifyCartLineItem>,
    newLTs: ShopifyCartLineItem[],
  ): ShopifyCartLineItem[] {
    if (oldLTs.length !== newLTs.length) {
      // TODO this could also be improved,
      oldLTs = newLTs
      return oldLTs
    }

    for (let i = 0; i < oldLTs.length; i++) {
      oldLTs[i] = this.mergeLineItem(oldLTs[i], newLTs[i])
    }

    return oldLTs
  }

  /** Merge the old cart object with a new one */
  protected static merge(newCart: ShopifyCartObject | null) {
    if (!newCart) {
      return this.cart
    }

    // Overwrite if falsy
    if (!this.cart) {
      this.cart = newCart
      return this.cart
    }

    for (const key of Object.keys(newCart) as Array<keyof ShopifyCartObject>) {
      if (key === 'items') {
        this.cart.items = this.mergeLineItems(this.cart.items, newCart.items)
      } else if (Array.isArray(this.cart[key] || newCart[key])) {
        // TODO merge arrays
        ;(this.cart[key] as ShopifyCartObject[typeof key]) = newCart[key]
      } else if (this.cart[key] !== newCart[key]) {
        ;(this.cart[key] as ShopifyCartObject[typeof key]) = newCart[key]
      }
    }

    return this.cart
  }

  /**
   * Use this to change cart attributes, the cart note, and quantities of line items in the cart.
   * @param id Variant ID
   * @param quantity Quantity
   * @param properties Additional properties
   * @return Response The JSON of the cart.
   * @see https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#update-cart
   */
  public static async update(
    id: number,
    quantity: number,
    properties: LineItemProperties = {},
    options: ShopifyCartRequestOptions = this.requestOptionDefaults,
  ): Promise<ShopifyCartObject> {
    if (options.triggerOnStart) {
      this.triggerOnStart()
    }

    const promise = this.queue
      .add(() => {
        const body: any = { id, quantity }
        if (Object.keys(properties).length > 0) {
          body.properties = properties
        }
        return HttpService.post(this.CART_POST_UPDATE_URL, body, 'form')
      })
      // because type is form we need to parse the json response by self
      .then((cart: string) => {
        return JSON.parse(cart)
      })
      .then((cart: ShopifyCartObject) => {
        if (options.triggerOnChange) {
          this.triggerOnChange(cart)
        }
        return cart
      })

    if (options.triggerOnComplete) {
      this.triggerOnComplete('update')
    }
    return promise
  }

  /**
   * Validates the cart items and updates all items to ensure that all are valid
   * @returns
   */
  public static async updateItems() {
    if (!this.cart) {
      return
    }

    const updates: ShopifyCartUpdateProperty = {}

    // Nothing to do here, because we don't validate the quantity anymore
    // for (const item of this.cart.items) {
    //   const key = item.key || item.variant_id
    //   updates[key] = item.quantity
    // }

    return this.updates(updates)
  }

  /**
   * Use this to change cart attributes, the cart note, and quantities of line items in the cart.
   *
   * @return Response The JSON of the cart.
   * @see https://shopify.dev/api/ajax/reference/cart#post-locale-cart-update-js
   */
  public static async updates(
    updates: ShopifyCartUpdateProperty | Array<number>,
    options: ShopifyCartRequestOptions = this.requestOptionDefaults,
  ): Promise<ShopifyCartObject> {
    if (options.triggerOnStart) {
      this.triggerOnStart()
    }

    const promise = this.queue
      .add(() => {
        return HttpService.post(
          this.CART_POST_UPDATE_URL,
          {
            updates,
          },
          'json',
        )
      })
      // because type is form we need to parse the json response by self
      .then((cart: string) => {
        return JSON.parse(cart)
      })
      .then((cart: ShopifyCartObject) => {
        if (options.triggerOnChange) {
          this.triggerOnChange(cart)
        }
        return cart
      })

    if (options.triggerOnComplete) {
      this.triggerOnComplete('update')
    }
    return promise
  }

  /**
   * This call sets the quantity of an item already in the cart.
   *
   * Although /cart/update.js and /cart/change.js may seem like they accomplish the same function,
   * they truly are quite different. The /cart/update.js controller allows updates to several items
   * at once, including items that may not yet be in the cart (it will add them), and it also allows
   * updates of cart attributes and the cart note. The /cart/change.js controller is only able to
   * update the quantity of one item at a time, and that item must be in the cart already. If the
   * item is not in the cart, /cart/change.js will not add it and it will then return a 404 error.
   * Whereas the /cart/update.js controller updates no quantity when any of the requested update
   * cannot be met, the /cart/change.js controller, on the other hand, will adjust the quantity to
   * add all items in stock if what is requested is greater than what's available. Use your browser's
   * JavaScript console to test things out if you're not sure about the behavior of the different request URLs.
   *
   * @param id Variant ID or Line item key
   * @param quantity Quantity
   * @return Response The JSON of the cart.
   * @see https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#change-cart
   */
  protected static async _change(
    id: number | string,
    quantity: number,
    options: ShopifyCartRequestOptions = this.requestOptionDefaults,
  ): Promise<ShopifyCartObject> {
    if (options.triggerOnStart) {
      this.triggerOnStart()
    }

    const promise = this.queue.add(async () => {
      const body: any = { id: id.toString(), quantity }

      const cartRes = await HttpService.post<ShopifyCartObject | string>(
        this.CART_POST_CHANGE_URL,
        body,
        'json',
      )

      if (cartRes.status >= 400) {
        // TODO parse error
        throw cartRes
      }

      // WORKAROUND: Addressing Shopify's Line Item Splitting and Pricing Issue
      //
      // Issue Description:
      // When a specific discount threshold is reached in Shopify, the platform splits line items in the shopping cart.
      // This splitting leads to an unusual behavior: attempting to decrease / increase the quantity of an item results in receiving
      // a cart object with incorrect pricing information. This behavior deviates from the expected functionality where
      // the cart should accurately reflect the updated quantity and corresponding prices.
      //
      // Workaround Strategy:
      // To circumvent this issue, the implemented workaround involves re-fetching the entire cart object after attempting
      // to update the quantity of a line item. By retrieving the complete and updated cart object, we ensure that the
      // pricing and item details are correctly synchronized and reflect the actual state of the cart post-update.
      // This approach is necessary to deal with the current limitations in Shopify's handling of cart updates under
      // specific discount conditions.
      //
      // Note:
      // This workaround is a temporary measure to address the current anomaly in Shopify's cart behavior. It's important
      // to monitor for any changes or updates from Shopify that might affect or resolve this issue, and adjust the
      // implementation accordingly.
      //
      const cart = await this.refresh()

      // Use this if the WORKAROUND is nit needed anymore
      // if (typeof cartRes.body === 'string') {
      //   cart = JSON.parse(cartRes.body)
      // } else {
      //   cart = cartRes.body
      // }

      if (options.triggerOnChange) {
        this.triggerOnChange(cart)
      }

      return cart
    })

    if (options.triggerOnComplete) {
      this.triggerOnComplete('change')
    }

    return promise
  }

  protected static _changeDebounced = debounce(this._change.bind(this), 500)

  /**
   * This call sets the quantity of an item already in the cart.
   *
   * Although /cart/update.js and /cart/change.js may seem like they accomplish the same function,
   * they truly are quite different. The /cart/update.js controller allows updates to several items
   * at once, including items that may not yet be in the cart (it will add them), and it also allows
   * updates of cart attributes and the cart note. The /cart/change.js controller is only able to
   * update the quantity of one item at a time, and that item must be in the cart already. If the
   * item is not in the cart, /cart/change.js will not add it and it will then return a 404 error.
   * Whereas the /cart/update.js controller updates no quantity when any of the requested update
   * cannot be met, the /cart/change.js controller, on the other hand, will adjust the quantity to
   * add all items in stock if what is requested is greater than what's available. Use your browser's
   * JavaScript console to test things out if you're not sure about the behavior of the different request URLs.
   *
   * @param id Variant ID or Line item key
   * @param quantity Quantity
   * @param properties Additional properties
   * @return Response The JSON of the cart.
   * @see https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#change-cart
   */
  public static async change(
    id: number | string,
    quantity: number,
    options: ShopifyCartRequestOptions = this.requestOptionDefaults,
  ): Promise<ShopifyCartObject> {
    return this._changeDebounced(id, quantity, options)
  }

  /**
   * If you use Line Item Properties you may end up with several items in the cart that share the same variant ID.
   * How do you update the quantity of an item in the cart that has specific line item properties?
   * Once you have identified the 1-based index of the item in the cart, you can use the line property instead of id.
   * @param line -based index of the item in the cart
   * @param quantity Quantity
   * @param properties Additional properties
   * @return Response The JSON of the cart.
   */
  public static async changeLine(
    line: string | number,
    quantity: number,
    properties: LineItemProperties = {},
    options: ShopifyCartRequestOptions = this.requestOptionDefaults,
  ): Promise<ShopifyCartObject> {
    if (options.triggerOnStart) {
      this.triggerOnStart()
    }

    const promise = this.queue.add(async () => {
      const body: any = { line, quantity }
      if (Object.keys(properties).length !== 0) {
        body.properties = properties
      }
      const cartRes = await HttpService.post<ShopifyCartObject | string>(
        this.CART_POST_CHANGE_URL,
        body,
        'form',
      )

      let cart: ShopifyCartObject

      // Because type is form we need to parse the json response by self
      if (typeof cartRes.body === 'string') {
        cart = JSON.parse(cartRes.body)
      } else {
        cart = cartRes.body
      }

      if (options.triggerOnChange) {
        this.triggerOnChange(cart)
      }

      return cart
    })

    if (options.triggerOnComplete) {
      this.triggerOnComplete('change')
    }
    return promise as Promise<ShopifyCartObject>
  }

  /**
   * This call sets all quantities of all line items in the cart to zero.
   * @return The JSON of an empty cart. This does not remove cart attributes nor the cart note.
   * @return Response The JSON of an empty cart. This does not remove cart attributes nor the cart note.
   * @see https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#clear-cart
   */
  public static async clear(
    options: ShopifyCartRequestOptions = this.requestOptionDefaults,
  ): Promise<ShopifyCartObject> {
    if (options.triggerOnStart) {
      this.triggerOnStart()
    }
    const promise = this.queue.add(async () => {
      const cartRes = await HttpService.post<ShopifyCartObject>(
        this.CART_POST_CLEAR_URL,
        {},
        'form',
      )
      let cart: ShopifyCartObject

      // Because type is form we need to parse the json response by self
      if (typeof cartRes.body === 'string') {
        cart = JSON.parse(cartRes.body)
      } else {
        cart = cartRes.body
      }

      if (options.triggerOnChange) {
        this.triggerOnChange(cart)
      }

      return cart
    })

    if (options.triggerOnComplete) {
      this.triggerOnComplete('clear')
    }
    return promise
  }

  public static async _getShippingRates(
    shippingAddress: ShopifyCustomerAddress,
    normalize = true,
  ): Promise<ShopifyShippingRates | ShopifyShippingRatesNormalized> {
    const res = await HttpService.get<{ shipping_rates: ShopifyShippingRates }>(
      this.CART_GET_SHIPPING_RATES_URL,
      { shipping_address: shippingAddress },
      'json',
    )

    const shippingRates = res.body

    if (isObject(shippingRates) && isObject(shippingRates.shipping_rates)) {
      if (normalize) {
        return this.normalizeShippingRates(shippingRates.shipping_rates)
      }
      return shippingRates.shipping_rates as ShopifyShippingRates
    } else {
      console.warn(
        'shipping_rates property not found: ' + JSON.stringify(shippingRates),
      )
      return []
    }
  }

  /**
   * Get estimated shipping rates.
   * @param shippingAddress TODO: /cart/shipping_rates.json?shipping_address[zip]=K1N 5T2&shipping_address[country]=Canada&shipping_address[province]=Ontario
   * @see https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#get-shipping-rates
   */
  public static async getShippingRates(
    shippingAddress: ShopifyCustomerAddress,
    normalize = true,
    options: ShopifyCartRequestOptions = this.requestOptionDefaults,
  ): Promise<ShopifyShippingRates | ShopifyShippingRatesNormalized> {
    if (options.triggerOnStart) {
      this.triggerOnStart()
    }
    const promise = this.queue.add(() => {
      return this._getShippingRates(shippingAddress, normalize)
    })

    if (options.triggerOnComplete) {
      this.triggerOnComplete('get')
    }
    return promise
  }

  public static requestOptionDefaults = {
    triggerOnStart: true,
    triggerOnComplete: true,
    triggerOnChange: true,
  }

  protected static waitForComplete = false

  protected static prepareLineItems(cart: ShopifyCartObject) {
    if (!cart?.items) {
      return cart
    }
    for (const item of cart?.items || []) {
      // Remove "Default Title" Option values
      item.variant_options = ShopifyProductService.renameOptionValues(
        item.variant_options,
      )
      item.normalizedImage = ShopifyProductService.normalizeImageObject(
        item.featured_image || item.image,
      )
    }
    return cart
  }

  /**
   * Trigger `ShopifyCart:request:complete`, if queue is already pending no noting (in this case we already looking for onIdle)
   */
  protected static async triggerOnComplete(action: ShopifyCartAction) {
    if (!this.waitForComplete) {
      this.waitForComplete = true
      return this.queue.onIdle().then(() => {
        this.events.trigger('ShopifyCart:request:complete', this.cart, action)
        this.waitForComplete = false
      })
    }
  }

  /**
   * TODO check if cart values are changed
   * @param cart The cart object
   */
  protected static triggerOnChange(cart: ShopifyCartObject | null) {
    if (!cart) {
      console.warn('The cart is falsy, do nothing!')
      return
    }
    this.set(cart)
    this.events.trigger('ShopifyCart:request:changed', this.cart)
  }

  /**
   * Trigger `ShopifyCart:request:start`, if not already triggered
   */
  protected static triggerOnStart() {
    if (this.queue.pending > 0) {
      return
    }
    this.events.trigger('ShopifyCart:request:start')
  }

  /**
   * Trigger `ShopifyCart:add`
   */
  protected static triggerAdd(
    product: ShopifyProduct,
    variantId: number,
    quantity: number,
    properties: LineItemProperties,
    cart: ShopifyCartObject,
  ) {
    this.events.trigger(
      'ShopifyCart:add',
      product,
      variantId,
      quantity,
      properties,
      cart,
    )
  }

  protected static normalizeShippingRates(
    shippingRates: ShopifyShippingRates,
  ): ShopifyShippingRatesNormalized {
    const normalized = new Array<any>(shippingRates.length)
    for (const i in shippingRates) {
      if (shippingRates[i]) {
        const shippingRate = shippingRates[i]
        normalized[i] = clone<ShopifyShippingRate>(false, shippingRate)
        if (normalized[i] && normalized[i].price) {
          normalized[i].price = getNumber(normalized[i].price)
          if (normalized[i].price) {
            normalized[i].price *= 100
          } else {
            console.warn(`Can't parse "${normalized[i].price}" to number`)
          }
        } else {
          console.warn(`price property not defined`, normalized[i])
        }
      }
    }
    return normalized as ShopifyShippingRatesNormalized
  }

  /**
   * Get the total quantity of a specific product variant in the cart.
   * @param variantId The ID of the product variant.
   * @param excludeLineItemKeys - An array of line item keys (uniq per line) to exclude from the count
   * @returns The total quantity of the variant in the cart.
   */
  public static getTotalVariantQuantityInCart(
    variantId: number,
    excludeLineItemKeys: string[] = [],
  ): number {
    if (!this.cart || !this.cart.items) {
      return 0
    }
    return this.cart.items
      .filter(
        item =>
          item.variant_id === variantId &&
          !excludeLineItemKeys.includes(item.key),
      )
      .reduce((total, item) => total + item.quantity, 0)
  }

  /**
   * Ensures quantity is within available inventory limits and minimum quantity rules
   * @param variant The variant to validate the quantity for
   * @param userInputQuantity The user input quantity
   * @param options Additional validation options
   * @param options.multipackQuantity The quantity of items in a multipack (defaults to 1)
   */
  public static validateQuantity(
    variant: ShopifyProductVariant,
    userInputQuantity: number,
    options: {
      multipackQuantity?: number
      excludeLineItemKeys?: string[]
      cartItemsCount?: number
    } = {},
  ): ValidateQuantityResult {
    const { multipackQuantity = 1, excludeLineItemKeys = [] } = options
    let cartItemsCount =
      options.cartItemsCount ??
      ShopifyCartService.getTotalVariantQuantityInCart(
        variant.id,
        excludeLineItemKeys,
      )

    let allowedUserInput = 0
    let isValid = false
    let validationError: ValidateQuantityResult['validationError'] = undefined

    const maxAvailable = ShopifyProductService.getMaxAvailableQuantity(variant)
    const { min } = ShopifyProductService.getQuantityRule(variant)
    const availableMultipackQuantity = Math.floor(
      (maxAvailable - cartItemsCount) / multipackQuantity,
    )

    // Calculate requested quantity without cart quantity
    const requestedQuantity = userInputQuantity * multipackQuantity

    // Calculate total requested quantity including cart quantity
    const totalRequestedQuantity = requestedQuantity + cartItemsCount

    // Check if quantity is within bounds (min and max)
    if (totalRequestedQuantity > maxAvailable) {
      // Handle maximum limit
      allowedUserInput = availableMultipackQuantity
      validationError = VALIDATE_QUANTITY_ERROR.MoreThanMax
      isValid = false
    } else if (totalRequestedQuantity < min || requestedQuantity <= 0) {
      // Check if requested quantity is less than min or 0
      // We no not allow 0 quantity because it makes no sense to add or buy 0 items
      allowedUserInput = Math.ceil(min / multipackQuantity)
      validationError = VALIDATE_QUANTITY_ERROR.LessThanMin
      isValid = false
    } else {
      allowedUserInput = userInputQuantity
      isValid = true
    }

    // Ensure quantity is not negative
    allowedUserInput = Math.max(0, allowedUserInput)

    const isMultipack = multipackQuantity > 1

    return {
      allowedUserInput,
      requestedQuantity,
      totalRequestedQuantity,
      maxAvailable,
      cartItemsCount,
      isValid,
      validationError,
      variant,
      isMultipack,
      multiplicator: isMultipack ? userInputQuantity : undefined,
      multipackQuantity: isMultipack ? multipackQuantity : undefined,
      availableMultipackQuantity,
      min,
    }
  }
}
